import { useQuery } from '@tanstack/react-query'
import { Button, Flex, Form, Input, Select, Typography } from 'antd'
import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { organizationsAccessListOptions, type TeamTypeEnum } from 'src/api/main'
import { ResizableDrawer } from 'src/components'
import { composeDisplayName } from 'src/utils/string'

import { useTeamMutations } from '../hooks'
import type { OrgNodeKind, OrgUnit } from '../types'
import { apiTypeToKind, kindToApiType } from '../utils'

const { Text } = Typography
const { TextArea } = Input

export type OrgFormMode =
  | { kind: 'create'; defaultType: OrgNodeKind; parent?: OrgUnit | null }
  | { kind: 'edit'; unit: OrgUnit }

type Props = {
  workspaceId: number
  open: boolean
  mode: OrgFormMode | null
  units: OrgUnit[]
  onClose: () => void
}

type FormValues = {
  name: string
  type: TeamTypeEnum
  parent?: number | null
  abbreviation?: string
  description?: string
  head_user?: number | null
}

export const OrgFormDrawer = ({
  workspaceId,
  open,
  mode,
  units,
  onClose
}: Props) => {
  const { t } = useTranslation()
  const [form] = Form.useForm<FormValues>()
  const { create, update } = useTeamMutations(workspaceId)

  const isEdit = mode?.kind === 'edit'

  // People options come from organization access list (already loaded elsewhere).
  const { data: accessPage } = useQuery({
    ...organizationsAccessListOptions({
      query: { organization: workspaceId, per_page: 500 }
    }),
    enabled: open && Number.isFinite(workspaceId)
  })

  const peopleOptions = useMemo(() => {
    return (accessPage?.results ?? []).map((m) => ({
      value: m.user.id,
      label: composeDisplayName(
        m.user.first_name,
        m.user.last_name,
        m.user.email
      )
    }))
  }, [accessPage])

  // Parent options: filtered by selected type — a department can only nest under
  // a division; a team only under a department; a division has no parent.
  const selectedType = Form.useWatch('type', form)
  const parentOptions = useMemo(() => {
    if (!selectedType) return []
    if (selectedType === 'branch') return []
    const requiredParentType: TeamTypeEnum =
      selectedType === 'department' ? 'branch' : 'department'

    return units
      .filter((u) => u.type === requiredParentType)
      .map((u) => ({ value: u.id, label: u.name }))
  }, [units, selectedType])

  useEffect(() => {
    if (!open || !mode) return
    if (mode.kind === 'edit') {
      form.setFieldsValue({
        name: mode.unit.name,
        type: mode.unit.type ?? 'section',
        parent: mode.unit.parent ?? null,
        abbreviation: mode.unit.abbreviation ?? '',
        description: mode.unit.description ?? '',
        head_user: mode.unit.head_user ?? null
      })
    } else {
      form.setFieldsValue({
        name: '',
        type: kindToApiType(mode.defaultType),
        parent: mode.parent?.id ?? null,
        abbreviation: '',
        description: '',
        head_user: null
      })
    }
  }, [open, mode, form])

  const handleSubmit = async (values: FormValues) => {
    if (!mode) return

    // Divisions are always top-level (parent = null) regardless of UI selection.
    const parent = values.type === 'branch' ? null : (values.parent ?? null)

    if (mode.kind === 'edit') {
      await update.mutateAsync({
        path: { id: mode.unit.id },
        body: {
          ...mode.unit,
          name: values.name,
          type: values.type,
          parent,
          abbreviation: values.abbreviation || null,
          description: values.description ?? '',
          head_user: values.head_user ?? null,
          organization: workspaceId
        }
      })
    } else {
      await create.mutateAsync({
        body: {
          name: values.name,
          type: values.type,
          parent,
          abbreviation: values.abbreviation || null,
          description: values.description ?? '',
          head_user: values.head_user ?? null,
          organization: workspaceId
        }
      })
    }
    onClose()
  }

  const submitting = create.isPending || update.isPending

  const title = isEdit
    ? t('organization.form.editTitle', {
        type: t(`organization.types.${apiTypeToKind(mode.unit.type)}`)
      })
    : t('organization.form.createTitle', {
        type: t(
          `organization.types.${apiTypeToKind(kindToApiType(mode?.defaultType ?? 'division'))}`
        )
      })

  return (
    <ResizableDrawer
      open={open}
      onClose={() => {
        if (!submitting) onClose()
      }}
      defaultWidth={520}
      storageKey="org-form-drawer-width"
      title={title}
      footer={
        <Flex justify="flex-end" gap={8}>
          <Button onClick={onClose} disabled={submitting}>
            {t('organization.form.cancel')}
          </Button>
          <Button
            type="primary"
            loading={submitting}
            onClick={() => form.submit()}
          >
            {isEdit
              ? t('organization.form.saveChanges')
              : t('organization.form.create')}
          </Button>
        </Flex>
      }
    >
      <Form<FormValues>
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        requiredMark="optional"
      >
        <Form.Item
          name="name"
          label={t('organization.form.fields.name')}
          rules={[
            { required: true, message: t('organization.form.required.name') }
          ]}
        >
          <Input placeholder={t('organization.form.placeholders.name')} />
        </Form.Item>

        <Form.Item
          name="type"
          label={t('organization.form.fields.type')}
          rules={[{ required: true }]}
        >
          <Select
            options={[
              {
                value: 'branch',
                label: t('organization.types.division')
              },
              {
                value: 'department',
                label: t('organization.types.department')
              },
              {
                value: 'section',
                label: t('organization.types.team')
              }
            ]}
          />
        </Form.Item>

        {selectedType !== 'branch' ? (
          <Form.Item
            name="parent"
            label={t('organization.form.fields.parent')}
            rules={[
              {
                required: true,
                message: t('organization.form.required.parent')
              }
            ]}
          >
            <Select
              showSearch={{ optionFilterProp: 'label' }}
              placeholder={t('organization.form.placeholders.parent')}
              options={parentOptions}
              disabled={parentOptions.length === 0}
              notFoundContent={
                parentOptions.length === 0 ? (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {t('organization.form.noParents', {
                      type: t(
                        `organization.types.${selectedType === 'department' ? 'division' : 'department'}`
                      )
                    })}
                  </Text>
                ) : null
              }
            />
          </Form.Item>
        ) : null}

        <Form.Item
          name="abbreviation"
          label={t('organization.form.fields.abbreviation')}
        >
          <Input maxLength={16} placeholder="ENG, MKT…" />
        </Form.Item>

        <Form.Item
          name="description"
          label={t('organization.form.fields.description')}
        >
          <TextArea
            rows={3}
            maxLength={400}
            showCount
            placeholder={t('organization.form.placeholders.description')}
          />
        </Form.Item>

        <Form.Item name="head_user" label={t('organization.form.fields.lead')}>
          <Select
            allowClear
            showSearch={{ optionFilterProp: 'label' }}
            options={peopleOptions}
            placeholder={t('organization.form.placeholders.lead')}
          />
        </Form.Item>
      </Form>
    </ResizableDrawer>
  )
}
